import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles, ChevronDown, ChevronRight, Pencil, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatMoney, formatMoney2, formatNumber } from "../lib/api";
import { ExportButtons } from "../lib/exports";
import { col, PRODUCT_COLS } from "../lib/exportCols";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function GroupProducts({ group, groups, onMove }) {
  return (
    <table className="dense" style={{ fontSize: 11 }} data-testid={`sust-products-${group.name}`}>
      <thead><tr><th>CN</th><th>Descripción</th><th>Laboratorio</th><th className="num">Stock</th>
        <th className="num">Valor stock</th><th className="num">Ventas</th><th className="num">Margen</th><th>ABCD</th><th>Mover a</th></tr></thead>
      <tbody>
        {group.products.map((p) => (
          <tr key={p.cn}>
            <td className="font-mono">{p.cn}</td>
            <td>{p.descripcion}</td>
            <td className="text-xs">{p.laboratorio}</td>
            <td className="num">{formatNumber(p.stock)}</td>
            <td className="num">{formatMoney2(p.valor_pvp)}</td>
            <td className="num">{formatMoney2(p.tot_venta)}</td>
            <td className="num">{p.pvp > 0 && p.pmc > 0 ? pct((p.pvp - p.pmc) / p.pvp) : "—"}</td>
            <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
            <td>
              <select className="text-xs border border-slate-200 rounded px-1 py-0.5 max-w-[140px]" value={group.name}
                onChange={(e) => onMove(p.cn, group.name, e.target.value)} data-testid={`sust-move-${p.cn}`}>
                {groups.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
              </select>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GroupRow({ g, totStock, open, onToggle, onRename, children }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(g.name);
  const vs = g.stock_eur > 0 ? g.ventas_eur / g.stock_eur : 0;
  const save = (e) => { e.stopPropagation(); setEditing(false); if (name.trim() && name !== g.name) onRename(g.name, name.trim()); };
  return (
    <>
      <tr className="cursor-pointer hover:bg-blue-50" onClick={onToggle} data-testid={`sust-group-${g.name}`}>
        <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="font-semibold">
          {editing ? (
            <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <input className="border border-slate-300 rounded px-1 py-0.5 text-sm" value={name} autoFocus
                onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save(e)} data-testid="sust-rename-input" />
              <button onClick={save} data-testid="sust-rename-save"><Check size={14} /></button>
            </span>
          ) : (
            <span className="flex items-center gap-2">{g.name}
              <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="text-slate-400 hover:text-slate-700" data-testid={`sust-rename-${g.name}`}><Pencil size={12} /></button>
            </span>
          )}
        </td>
        <td className="num">{formatNumber(g.refs)}</td>
        <td className="num">{formatNumber(g.labs)}</td>
        <td className="num">{formatNumber(g.stock_uds)}</td>
        <td className="num font-bold">{formatMoney(g.stock_eur)}</td>
        <td className="num text-slate-600">{totStock > 0 ? pct(g.stock_eur / totStock) : "—"}</td>
        <td className="num">{formatMoney(g.ventas_eur)}</td>
        <td className="num">{formatNumber(g.uds_vendidas)}</td>
        <td className={"num font-bold " + (vs >= 1 ? "text-emerald-700" : "text-red-600")}>{vs > 0 ? `${vs.toFixed(2)}×` : "—"}</td>
        <td className="num">{pct(g.margen_medio)}</td>
      </tr>
      {open && <tr><td colSpan={11} className="bg-slate-50 p-2">{children}</td></tr>}
    </>
  );
}

export default function SustitutivosFamilia({ studyId, familia }) {
  const [data, setData] = useState({ status: "loading", groups: [] });
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/studies/${studyId}/substitutes`, { params: { familia } });
    setData(d);
    return d.status;
  }, [studyId, familia]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data.status !== "running") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data.status, load]);

  const generate = async () => {
    await api.post(`/studies/${studyId}/substitutes/generate`, { familia });
    setData({ status: "running", groups: [] });
  };

  const persist = async (groups) => {
    await api.put(`/studies/${studyId}/substitutes`, { familia, groups });
    await load();
  };
  const toPayload = (groups) => groups.map((g) => ({ name: g.name, cns: g.products.map((p) => p.cn) }));

  const rename = (from, to) => {
    if (data.groups.some((g) => g.name === to)) return toast.error("Ya existe un grupo con ese nombre");
    persist(toPayload(data.groups).map((g) => (g.name === from ? { ...g, name: to } : g))).then(() => toast.success("Grupo renombrado"));
  };
  const move = (cn, from, to) => {
    if (from === to) return;
    const groups = toPayload(data.groups).map((g) => ({
      ...g, cns: g.name === from ? g.cns.filter((c) => c !== cn) : g.name === to ? [...g.cns, cn] : g.cns,
    }));
    persist(groups).then(() => toast.success(`Producto movido a ${to}`));
  };

  if (data.status === "loading") return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>;

  if (data.status === "running") return (
    <div className="p-10 text-center text-slate-600" data-testid="sust-running">
      <Loader2 className="animate-spin mx-auto text-blue-600" size={28} />
      <p className="mt-3 font-semibold">La IA está agrupando los productos de parafarmacia de {familia}…</p>
      <p className="text-xs text-slate-500 mt-1">Suele tardar entre 30 segundos y 2 minutos. Puedes esperar aquí.</p>
    </div>
  );

  if (data.status !== "done") return (
    <div className="p-10 text-center" data-testid="sust-empty">
      <Sparkles className="mx-auto text-blue-600" size={28} />
      <p className="mt-3 text-slate-700 font-semibold">Agrupa la parafarmacia de {familia} en sustitutivos</p>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">La IA lee las descripciones y junta los productos que cubren la misma necesidad (p. ej. pastillas para la garganta) para ver cuánto stock tienes de cada una.</p>
      {data.status === "error" && <p className="text-sm text-red-600 mt-2" data-testid="sust-error">No se pudo agrupar: {data.error}. Inténtalo de nuevo.</p>}
      <button onClick={generate} className="btn btn-primary btn-sm mt-4" data-testid="sust-generate-btn"><Sparkles size={14} /> Agrupar con IA</button>
    </div>
  );

  const totStock = data.groups.reduce((a, g) => a + g.stock_eur, 0);
  return (
    <div data-testid="sust-panel">
      <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 text-xs text-blue-900 flex items-center justify-between gap-3 flex-wrap">
        <span><b>{data.groups.length} grupos de sustitutivos</b> · solo parafarmacia con stock o ventas · pulsa un grupo para ver sus productos</span>
        <div className="flex gap-2 flex-wrap">
          <ExportButtons filename={`sustitutivos-${familia}`} title={`Sustitutivos de parafarmacia · ${familia}`} sheetName="Sustitutivos" testIdPrefix="sust-export"
                         rows={data.groups.flatMap((g) => g.products.map((p) => ({ ...p, grupo: g.name })))}
                         columns={[col.text("Grupo", "grupo"), ...PRODUCT_COLS.filter((c) => c.key !== "familia")]} orientation="landscape" />
          <button onClick={generate} className="btn btn-outline btn-sm" data-testid="sust-regenerate-btn"><RefreshCw size={12} /> Reagrupar con IA</button>
        </div>
      </div>
      {data.groups.length === 0 ? (
        <p className="p-10 text-center text-slate-500" data-testid="sust-no-products">Esta familia no tiene productos de parafarmacia con stock o ventas.</p>
      ) : (
        <table className="dense" data-testid="sust-groups-table">
          <thead><tr><th></th><th>Grupo</th><th className="num">Refs.</th><th className="num">Labs.</th><th className="num">Stock uds.</th>
            <th className="num">Valor stock</th><th className="num">% stock</th><th className="num">Ventas</th><th className="num">Uds. vend.</th>
            <th className="num">V/S</th><th className="num">Margen medio</th></tr></thead>
          <tbody>
            {data.groups.map((g) => (
              <GroupRow key={g.name} g={g} totStock={totStock} open={open === g.name}
                onToggle={() => setOpen(open === g.name ? null : g.name)} onRename={rename}>
                <GroupProducts group={g} groups={data.groups} onMove={move} />
              </GroupRow>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
